import { useState } from "react";
import {
  convertedImageToTextPost,
  retrieveVehicleLicensePlate,
} from "../../common/services/vehicles.api";
import { useToastContext } from "../../common/components/ui/toast/toast.context";
import { IVehicle, VEHICLE_STATUS } from "../../types/vehicle.type";
import { rentersFindByLincesePlate } from "../../common/services/renters.api";
import { IRenter } from "../../types/renter.type";
import { IVehicleLog } from "../../types/vehicleLog";
import { vehicleLogsFindByLincesePlate } from "../../common/services/logs.api";
import { InfoTable } from "../../common/components/ui/table/InfoTable";
import { getStatusLabelsRenters } from "../../modules/renters/components/table-renters/TableRenters";
import Permission from "../../common/components/auth/Permission";
import {
  getStatusLabelsVehicle,
  STATUS_LABELS,
} from "../../modules/vehicles/components/TableVehicles";
import { formatDateAndTime } from "../../common/helpers/date.helpers";
import { FiDollarSign, FiPackage, FiSearch } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { PiMotorcycleFill } from "react-icons/pi";
import { SiGoogleadsense } from "react-icons/si";
import { MdSyncAlt } from "react-icons/md";
import ModalChangeStatusVehicle from "@/modules/vehicles/components/modals/ChangeStatusVehicle";
import { AiFillTool } from "react-icons/ai";
import ModalCreateMaintenance from "@/modules/maintenances/components/modals/ModalCreateMaintenance";
import { GrHostMaintenance } from "react-icons/gr";
import { FaLocationCrosshairs } from "react-icons/fa6";

// eslint-disable-next-line react-refresh/only-export-components
export function readFileAsBase64(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (
        reader.result &&
        typeof reader.result === "string" &&
        reader.result.includes(",")
      ) {
        const base64Data = reader.result.split(",")[1];
        resolve(base64Data);
      } else {
        reject(new Error("Invalid file format or corrupted file."));
      }
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsDataURL(file);
  });
}

const STATUS_VEHICLE_COLORS = {
  [VEHICLE_STATUS.AVAILABLE]: "bg-greenPrimary text-white",
  [VEHICLE_STATUS.RENTED]: "bg-bluePrimary text-white",
};

function getLabelStatusVehicle(status: VEHICLE_STATUS) {
  const colorClass =
    STATUS_VEHICLE_COLORS[status] ?? "bg-redPrimary text-white";

  return (
    <span className={`px-2 p-1 rounded-md ${colorClass}`}>
      {STATUS_LABELS[status] ?? "Status desconhecido"}
    </span>
  );
}

const formatLogs = (logs: IVehicleLog[]): IVehicleLog[] => {
  return logs.map((log: IVehicleLog) => {
    const formattedLog: IVehicleLog = {
      ...log,
      meta: {
        ...log.meta,
        status: getStatusLabelsVehicle(log.meta.status as VEHICLE_STATUS),
        previousStatus: getStatusLabelsVehicle(
          log.meta.previousStatus as VEHICLE_STATUS
        ),
      },
      timestamp: formatDateAndTime(
        new Date(
          typeof log.timestamp === "string"
            ? new Date(log.timestamp).getTime() -
            (Number(log.durationSinceLastStatus) || 0)
            : (log.timestamp as Date).getTime() -
            (Number(log.durationSinceLastStatus) || 0)
        )
      ),
    };

    return formattedLog;
  });
};

function VehicleSituation() {
  const EMPTY_ACTION = { eventName: "", data: undefined };
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { notifySuccess, notifyError } = useToastContext();
  const [valueIVehicle, setValueIVehicle] = useState<IVehicle | null>(null);
  const [valueRenter, setValueRenter] = useState<IRenter | null>(null);
  const [valueVehicleLogs, setValueVehicleLog] = useState<IVehicleLog[]>([]);
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const [licensePlate, setLicensePlate] = useState<string | null>(null);

  const [showVehicleInfo, setShowVehicleInfo] = useState(false);
  const [showCustomerInfo, setShowCustomerInfo] = useState(false);
  const [showLogsInfo, setShowLogsInfo] = useState(false);

  const handleFileInput = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsLoading(true);
    setShowVehicleInfo(false);
    setShowCustomerInfo(false);
    setShowLogsInfo(false);

    const file = event.target.files?.[0];
    if (!file) {
      notifyError("Selecione um arquivo!");
      return;
    }

    try {
      const base64Data = await readFileAsBase64(file);
      if (!base64Data) {
        notifyError("Erro ao converter arquivo para base64");
        return;
      }

      const licensePlateResp = await convertedImageToTextPost(base64Data);
      if (!licensePlateResp.ok) {
        notifyError(
          licensePlateResp.message || "Falha ao extrair texto da imagem"
        );
        return;
      }

      const plate = licensePlateResp.data;
      setLicensePlate(plate);
      notifySuccess("Placa do veículo extraída com sucesso");
      await getData(plate);
    } catch (error) {
      notifyError("Ocorreu um erro ao processar o arquivo");
    } finally {
      setIsLoading(false);
    }
  };

  const getData = async (licensePlate: string) => {
    if (!licensePlate) {
      notifyError("Placa do veículo não encontrada");
      return;
    }

    const vehicleResp = await retrieveVehicleLicensePlate(licensePlate);
    if (!vehicleResp.ok) {
      notifyError(vehicleResp.message || "Falha ao localizar o veículo");
      return;
    }

    setValueIVehicle(vehicleResp.data);
    notifySuccess("Veículo localizado com sucesso");
    setShowVehicleInfo(true);

    const renterResp = await rentersFindByLincesePlate(
      vehicleResp.data.licensePlate
    );

    if (renterResp.ok && renterResp.data) {
      setValueRenter(renterResp.data);
      setShowCustomerInfo(true);
      notifySuccess("Cliente localizado com sucesso");
    } else {
      setShowCustomerInfo(false);
      notifyError(renterResp.message || "Falha ao localizar o cliente");
    }

    const logsResp = await vehicleLogsFindByLincesePlate(licensePlate);

    if (logsResp.ok) {
      setValueVehicleLog(formatLogs(logsResp.data));
      setShowLogsInfo(true);
      notifySuccess("Histórico do veículo localizado com sucesso");
    } else {
      setShowLogsInfo(false);
      notifyError("Falha ao recuperar histórico do veículo");
    }
  };

  const isAction = (eventName: string) => action.eventName === eventName;
  const cleanActionData = async () => {
    setAction(EMPTY_ACTION);
    setShowVehicleInfo(false);
    setShowCustomerInfo(false);
    setShowLogsInfo(false);
    await getData(licensePlate || "");
  };

  return (
    <>
      <Permission permission="vehicle:audit">
        <div className="px-4 pt-4 pb-2 space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* Verificar Veículo – sempre visível */}
            <div>
              <button
                type="button"
                onClick={() => document.getElementById("fileInput")?.click()}
                disabled={isLoading}
                className="flex flex-col items-center justify-center w-full h-24 sm:h-28 bg-gradient-to-r from-locavibe to-red-500 text-white rounded-2xl shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin mb-2" />
                    <span className="text-xs sm:text-sm font-medium">Verificando...</span>
                  </>
                ) : (
                  <>
                    <span className="text-3xl sm:text-4xl mb-1.5">
                      <FiSearch />
                    </span>
                    <span className="text-xs sm:text-sm font-medium">Verificar Veículo</span>
                  </>
                )}
              </button>
              <input
                id="fileInput"
                type="file"
                onChange={handleFileInput}
                accept="image/*"
                capture="environment"
                className="hidden"
              />
            </div>

            {!showVehicleInfo ? (
              <>
                <button
                  onClick={() => navigate("/dashboard/frota")}
                  className="flex flex-col items-center justify-center w-full h-24 sm:h-28 bg-gradient-to-r from-locavibe to-red-500 text-white rounded-2xl shadow-md transition-all duration-200"
                >
                  <span className="text-3xl sm:text-4xl mb-1.5">
                    <PiMotorcycleFill />
                  </span>
                  <span className="text-xs sm:text-sm font-medium">Frota</span>
                </button>
                <button
                  onClick={() => navigate("/dashboard/leads")}
                  className="flex flex-col items-center justify-center w-full h-24 sm:h-28 bg-gradient-to-r from-locavibe to-red-500 text-white rounded-2xl shadow-md transition-all duration-200"
                >
                  <span className="text-3xl sm:text-4xl mb-1.5">
                    <SiGoogleadsense />
                  </span>
                  <span className="text-xs sm:text-sm font-medium">Comercial</span>
                </button>
                <button
                  onClick={() => navigate("/dashboard/manutencao")}
                  className="flex flex-col items-center justify-center w-full h-24 sm:h-28 bg-gradient-to-r from-locavibe to-red-500 text-white rounded-2xl shadow-md transition-all duration-200"
                >
                  <span className="text-3xl sm:text-4xl mb-1.5">
                    <GrHostMaintenance />
                  </span>
                  <span className="text-xs sm:text-sm font-medium">Manutenção</span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() =>
                    setAction({ eventName: "changeStatus", data: valueIVehicle })
                  }
                  className="flex flex-col items-center justify-center w-full h-24 sm:h-28 bg-gradient-to-r from-locavibe to-red-500 text-white rounded-2xl shadow-md transition-all duration-200"
                >
                  <span className="text-3xl sm:text-4xl mb-1.5">
                    <MdSyncAlt />
                  </span>
                  <span className="text-xs sm:text-sm font-medium">Trocar Status</span>
                </button>

                {showVehicleInfo && (
                  <button
                    onClick={() =>
                      setAction({
                        eventName: "createMaintenance",
                        data: valueIVehicle,
                      })
                    }
                    className="flex flex-col items-center justify-center w-full h-24 sm:h-28 bg-gradient-to-r from-locavibe to-red-500 text-white rounded-2xl shadow-md transition-all duration-200"
                  >
                    <span className="text-3xl sm:text-4xl mb-1.5">
                      <AiFillTool />
                    </span>
                    <span className="text-xs sm:text-sm font-medium">Criar Manutenção</span>
                  </button>
                )}
              </>
            )}
          </div>

          {/* ── Links externos ──────────────────────────────────── */}
          {!showVehicleInfo && (
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-[11px] text-gray-400 font-semibold uppercase tracking-widest select-none">
                  Links externos
                </span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <button
                  onClick={() =>
                    window.open("https://app.vibetrack.com.br", "_blank")
                  }
                  className="flex flex-col items-center justify-center w-full h-24 sm:h-28 bg-gradient-to-r from-locavibe to-red-500 text-white rounded-2xl shadow-md transition-all duration-200"
                >
                  <span className="text-3xl sm:text-4xl mb-1.5">
                    <FaLocationCrosshairs />
                  </span>
                  <span className="text-xs sm:text-sm font-medium">VibeTrack</span>
                </button>

                <button
                  onClick={() =>
                    window.open("https://distribuicao.locavibe.com.br", "_blank")
                  }
                  className="flex flex-col items-center justify-center w-full h-24 sm:h-28 bg-gradient-to-r from-locavibe to-red-500 text-white rounded-2xl shadow-md transition-all duration-200"
                >
                  <span className="text-3xl sm:text-4xl mb-1.5">
                    <FiPackage />
                  </span>
                  <span className="text-xs sm:text-sm font-medium">Distribuição</span>
                </button>

                <button
                  onClick={() =>
                    window.open("https://financeiro.locavibe.com.br", "_blank")
                  }
                  className="flex flex-col items-center justify-center w-full h-24 sm:h-28 bg-gradient-to-r from-locavibe to-red-500 text-white rounded-2xl shadow-md transition-all duration-200"
                >
                  <span className="text-3xl sm:text-4xl mb-1.5">
                    <FiDollarSign />
                  </span>
                  <span className="text-xs sm:text-sm font-medium">Financeiro</span>
                </button>

                <button
                  onClick={() =>
                    window.open("https://parceiros.locavibe.com.br", "_blank")
                  }
                  className="flex flex-col items-center justify-center w-full h-24 sm:h-28 bg-gradient-to-r from-locavibe to-red-500 text-white rounded-2xl shadow-md transition-all duration-200"
                >
                  <span className="text-3xl sm:text-4xl mb-1.5">
                    <img src="/icon-signin.svg" alt="Parceiros" className="w-9 h-9 sm:w-10 sm:h-10" />
                  </span>
                  <span className="text-xs sm:text-sm font-medium">Parceiros</span>
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="border-t mt-4 md:mt-8 mx-2 md:mx-8 lg:mx-16">
          {(showVehicleInfo || showLogsInfo || showCustomerInfo) && (
            <div className="mt-4 border border-gray-200 rounded-lg p-4 relative bg-white shadow-sm">
              <button
                onClick={() => {
                  setShowVehicleInfo(false);
                  setShowLogsInfo(false);
                  setShowCustomerInfo(false);
                }}
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {showVehicleInfo && (
                <InfoTable
                  title="Informações do Veículo"
                  data={[
                    {
                      label: "Placa",
                      value: valueIVehicle?.licensePlate || "Não disponível",
                    },
                    {
                      label: "Proprietário",
                      value:
                        valueIVehicle?.partnerInfo.companyName ||
                        "Não disponível",
                    },
                    {
                      label: "Hodômetro",
                      value:
                        valueIVehicle?.odometerLocavibe !== undefined &&
                          valueIVehicle?.odometerLocavibe !== null
                          ? Number(
                            valueIVehicle.odometerLocavibe
                          ).toLocaleString("pt-BR")
                          : "Não disponível",
                    },
                    {
                      label: "Status",
                      value: valueIVehicle?.status ? (
                        <>
                          {getLabelStatusVehicle(valueIVehicle.status)}
                          <span className="ml-2 text-sm text-gray-600">
                            {" - Desde " +
                              (valueVehicleLogs?.[0]?.timestamp ||
                                "Data desconhecida")}
                          </span>
                        </>
                      ) : (
                        "Não disponível"
                      ),
                    },
                    ...(valueIVehicle?.status !== VEHICLE_STATUS.RENTED
                      ? [
                        {
                          label: "Tempo na Base",
                          value: valueIVehicle?.["daysAtBase"]
                            ? `${valueIVehicle["daysAtBase"]} dias`
                            : "Não disponível",
                        },
                      ]
                      : []),
                    {
                      label: "Tempo de Vida",
                      value: valueIVehicle?.createdAt
                        ? `Ano: ${valueIVehicle.year} - ${Math.floor(
                          (new Date().getTime() -
                            new Date(valueIVehicle.createdAt).getTime()) /
                          (1000 * 60 * 60 * 24)
                        )} dias`
                        : "Não disponível",
                    },
                  ]}
                />
              )}

              {showCustomerInfo && (
                <InfoTable
                  title="Informações do Cliente"
                  data={[
                    {
                      label: "Nome",
                      value: valueRenter?.name || "Não disponível",
                    },
                    {
                      label: "WhatsApp",
                      value: valueRenter?.whatsapp || "Não disponível",
                    },
                    {
                      label: "Status",
                      value: valueRenter?.status
                        ? getStatusLabelsRenters(valueRenter.status)
                        : "Não disponível",
                    },
                    {
                      label: "Situação",
                      value: valueRenter?.situation || "Não disponível",
                    },
                    {
                      label: "Plano",
                      value:
                        valueRenter?.activationData?.plan?.label ||
                        "Não disponível",
                    },
                  ]}
                />
              )}

              {showLogsInfo && (
                <div className="generic-table">
                  <table className="generic-table-style">
                    <thead className="generic-table-thead">
                      <tr>
                        <th key="1" className="generic-table-header">
                          <div className="flex justify-center align-middle">
                            <div className="generic-table-header-title">
                              Data/Hora do Registro
                              <p className="text-sm font-normal text-gray-600 dark:text-gray-600">
                                Quando aconteceu
                              </p>
                            </div>
                          </div>
                        </th>
                        <th key="2" className="generic-table-header">
                          <div className="flex justify-center align-middle">
                            <div className="generic-table-header-title">
                              Status Anterior
                              <p className="text-sm font-normal text-gray-600 dark:text-gray-600">
                                Como estava
                              </p>
                            </div>
                          </div>
                        </th>
                        <th key="4" className="generic-table-header">
                          <div className="flex justify-center align-middle">
                            <div className="generic-table-header-title">
                              Novo Status
                              <p className="text-sm font-normal text-gray-600 dark:text-gray-600">
                                Como ficou
                              </p>
                            </div>
                          </div>
                        </th>
                        <th key="5" className="generic-table-header">
                          <div className="flex justify-center align-middle">
                            <div className="generic-table-header-title">
                              Tempo no Status
                              <p className="text-sm font-normal text-gray-600 dark:text-gray-600">
                                Quanto tempo ficou no Status Anterior
                              </p>
                            </div>
                          </div>
                        </th>
                        <th key="6" className="generic-table-header">
                          <div className="flex justify-center align-middle">
                            <div className="generic-table-header-title">
                              Responsável pela Alteração
                              <p className="text-sm font-normal text-gray-600 dark:text-gray-600">
                                Quem fez
                              </p>
                            </div>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {valueVehicleLogs.length === 0 ? (
                        <tr className={"bg-white grid-cols-2"}>
                          <td className="py-3 text-center" colSpan={5}>
                            Nenhum registro encontrado.
                          </td>
                        </tr>
                      ) : (
                        valueVehicleLogs.map((item, index) => (
                          <tr
                            key={Date.now().toString() + index}
                            className={`${index % 2 ? "bg-gray2/10" : "bg-white"
                              }`}
                          >
                            <td
                              key={1 + index + Date.now().toString()}
                              className="generic-table-th dark:text-black"
                            >
                              {item?.timestamp
                                ? typeof item.timestamp === "string"
                                  ? item.timestamp
                                  : item.timestamp instanceof Date
                                    ? item.timestamp.toLocaleString()
                                    : "Status não disponível"
                                : "Status não disponível"}
                            </td>
                            <td
                              key={2 + index + Date.now().toString()}
                              className="generic-table-th dark:text-black"
                            >
                              {item?.meta?.previousStatus ||
                                "Status não disponível"}
                            </td>
                            <td
                              key={3 + index + Date.now().toString()}
                              className="generic-table-th dark:text-black"
                            >
                              {item?.meta?.status || "Status não disponível"}
                            </td>
                            <td
                              key={4 + index + Date.now().toString()}
                              className="generic-table-th dark:text-black"
                            >
                              {item?.durationSinceLastStatus
                                ? String(item.durationSinceLastStatus)
                                  .split("(")[0]
                                  .trim()
                                : "-"}
                            </td>
                            <td
                              key={4 + index + Date.now().toString()}
                              className="generic-table-th dark:text-black"
                            >
                              {item?.meta?.author || "Status não disponível"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          {isAction("changeStatus") && (
            <ModalChangeStatusVehicle
              vehicle={{
                licensePlate: (action.data as IVehicle).licensePlate,
                status: (action.data as IVehicle).status,
              }}
              onClose={cleanActionData}
            />
          )}

          {isAction("createMaintenance") && (
            <ModalCreateMaintenance
              readonly={true}
              licensePlate={valueIVehicle?.licensePlate}
              onClosed={cleanActionData}
              onSave={() => navigate("/manutencao")}
            />
          )}
        </div>
      </Permission>
    </>
  );
}

export default VehicleSituation;
